import { KeyboardArrowLeft as KeyboardArrowLeftIcon } from '@mui/icons-material';
import { Button, CircularProgress } from '@mui/material';
import { useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { CustomButton } from 'src/components/button/Button';
import { Snackbar } from 'src/components/snackbar';
import { GenericStepper } from 'src/components/stepper';
import { SCREEN_CONFIGURATION_LABELS } from 'src/constants';
import { PATHS } from 'src/constants/paths';
import {
  useGetAllLayoutType,
  useGetProjectConfig,
  useViewChannels,
} from 'src/hooks';
import { useCheckLayoutName } from 'src/hooks/api/layouts/useCheckLayoutName';
import {
  ILayoutPayload,
  useCreateTempLayout,
} from 'src/hooks/api/layouts/useCreateTempLayout';
import { useNavigateBlocker } from 'src/hooks/navigation';
import { LayoutTypes } from 'src/interfaces';
import { getScreenConfigurationLabel, lang } from 'src/utils';

import { WizardStep } from './AddLayoutsPage.interface';
import {
  ButtonsContainer,
  FormContainer,
  RightBtnContainer,
  Wrapper,
} from './AddLayoutsPage.styled';
import { AdvancedSettings } from './modules/advancedSettings/AdvancedSettings';
import { GeneralSettings } from './modules/generalSettings/GeneralSettings';
import { ScreenSetup } from './modules/screenSetup/ScreenSetup';

export const AddLayoutsPage = () => {
  const navigate = useNavigate();

  const [activeStep, setActiveStep] = useState(WizardStep.General);
  const [loading, setLoading] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState('');
  const { data: projectConfig } = useGetProjectConfig();

  const { data: layoutTypes = [] } = useGetAllLayoutType();
  const checkLayoutName = useCheckLayoutName();
  const createTempLayout = useCreateTempLayout();
  const { data: channelInfo } = useViewChannels();

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    watch,
    resetField,
    reset,
    formState: { errors, isDirty },
  } = useForm<ILayoutPayload>();
  useNavigateBlocker(isDirty);

  const selectedChannelInfo = channelInfo?.find(
    (channel) => channel.channel_name === selectedChannel,
  );
  const selectedLayoutType = layoutTypes.find(
    (type) => type.id === Number(watch('layout_type_id')),
  );
  const isDigitalMerch =
    selectedLayoutType?.name === LayoutTypes.DIGITAL_MERCHANDISER;
  const isOutdoor = selectedLayoutType?.name === LayoutTypes.OUTDOOR;

  useEffect(() => {
    setValue('layout_type_id', selectedChannelInfo?.layout_type_id[0]);
  }, [selectedChannelInfo, setValue]);

  const disclaimerFields = watch('disclaimers');

  const isEnableCustomerOrderDisplay = watch('enableCustomerOrderDisplay');

  useEffect(() => {
    if (!isEnableCustomerOrderDisplay) {
      setValue('enableDynamicArea', false);
      setValue('enableCodTriggeredArea', false);
      setValue('enablePromos', false);
      setValue('enableThankYou', false);
    }
  }, [isEnableCustomerOrderDisplay, setValue]);

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep((prev) => prev - 1);
    }
  };

  const handleChannelChange = (name: string) => {
    selectedChannelInfo?.screens?.map((screen, index) => {
      resetField(`grid_${index}`);
      resetField(`disclaimers.${index}`);
      resetField(`placement_${index}`);
      resetField(`size_${index}`);
      resetField(`layout_type_id`);
      resetField(`screen_layout_type`);

      return screen;
    });
    setSelectedChannel(name);
  };

  const StepFooter = ({
    handleNext,
    handleBack,
    loading,
  }: {
    handleNext: () => void;
    handleBack: () => void;
    loading: boolean;
  }) => {
    return (
      <ButtonsContainer>
        <Button
          onClick={handleBack}
          startIcon={<KeyboardArrowLeftIcon />}
          variant="outlined"
          style={
            activeStep === WizardStep.General ? { visibility: 'hidden' } : {}
          }>
          {lang.addLayoutsPage.buttons.back}
        </Button>
        <RightBtnContainer>
          <CustomButton
            onClick={() => navigate(PATHS.ASSEMBLY_LAYOUTS)}
            variant="text"
            color="error">
            {lang.addLayoutsPage.buttons.cancel}
          </CustomButton>
          <Button
            variant="contained"
            color="primary"
            type="submit"
            onClick={handleNext}
            disabled={loading}>
            {loading ? (
              <CircularProgress size={24} />
            ) : activeStep === steps.length - 1 ? (
              lang.addLayoutsPage.buttons.save
            ) : (
              lang.addLayoutsPage.buttons.next
            )}
          </Button>
        </RightBtnContainer>
      </ButtonsContainer>
    );
  };

  const handleNextGeneral = handleSubmit(async () => {
    const screenConfigLabel = getScreenConfigurationLabel(
      selectedChannelInfo?.screens ?? [],
    );
    if (
      isOutdoor &&
      screenConfigLabel === SCREEN_CONFIGURATION_LABELS.TWO_HORIZONTAL_SCREENS
    ) {
      Snackbar.snackbar(
        lang.addLayoutsPage.outdoorTwoHorizontalScreensError,
        'error',
        6 * 1000,
      );

      return;
    }

    setLoading(true);
    try {
      const isLayoutNameAvailable = await checkLayoutName.mutateAsync({
        ...getValues(),
      });
      if (isLayoutNameAvailable) {
        setActiveStep((prev) => prev + 1);
      } else {
        Snackbar.error(lang.addLayoutsPage.layoutExistsError);
      }
    } catch (error) {
      Snackbar.error(lang.addLayoutsPage.errorMessage);
    } finally {
      setLoading(false);
    }
  });

  const handleNextScreenConfig = handleSubmit(() => {
    const disclaimerFields = watch('disclaimers');
    const hasDisclaimerEnabled = disclaimerFields?.some(
      (disclaimer: any) => disclaimer,
    );

    if (!hasDisclaimerEnabled && !isDigitalMerch) {
      Snackbar.error(lang.addLayoutsPage.disclaimerError);
      setLoading(false);

      return;
    }

    setActiveStep((prev) => prev + 1);
  });

  const handleNextAdvanced = handleSubmit(async () => {
    setLoading(true);
    const payload = {
      ...getValues(),
      selectedChannelInfo,
      ...(selectedChannelInfo?.layoutType?.name === LayoutTypes.OUTDOOR &&
        projectConfig?.codPosition && {
          codPosition: projectConfig.codPosition,
        }),
    };
    try {
      const result = await createTempLayout.mutateAsync(payload);
      flushSync(() => {
        reset(undefined, { keepValues: true });
      });
      Snackbar.success(
        lang.addLayoutsPage.successMessage(getValues().name ?? ''),
      );
      navigate({
        pathname: PATHS.ASSEMBLY_LAYOUTS_EDIT(result.id),
      });
    } catch (error) {
      Snackbar.error(lang.addLayoutsPage.errorMessage);
      navigate(PATHS.ASSEMBLY_LAYOUTS);
    } finally {
      setLoading(false);
    }
  });

  const steps = [
    {
      name: lang.addLayoutsPage.steps.general,
      title: lang.addLayoutsPage.title,
      component: (
        <FormContainer>
          <GeneralSettings
            control={control}
            handleChannelChange={handleChannelChange}
            channelInfo={channelInfo}
            setValue={setValue}
            selectedChannelInfo={selectedChannelInfo}
            watch={watch}
          />
        </FormContainer>
      ),
      footer: (
        <StepFooter
          loading={loading}
          handleBack={handleBack}
          handleNext={handleNextGeneral}
        />
      ),
    },
    {
      name: lang.addLayoutsPage.steps.screenConfig,
      title: lang.addLayoutsPage.title,
      component: (
        <FormContainer>
          <ScreenSetup
            control={control}
            errors={errors}
            getValues={getValues}
            setValue={setValue}
            watch={watch}
            selectedChannelInfo={selectedChannelInfo}
            selectedLayoutType={selectedLayoutType}
            disclaimerFields={disclaimerFields}
            isOutdoor={isOutdoor}
            codPosition={projectConfig?.codPosition}
          />
        </FormContainer>
      ),
      footer: (
        <StepFooter
          loading={loading}
          handleBack={handleBack}
          handleNext={handleNextScreenConfig}
        />
      ),
    },
    {
      name: lang.addLayoutsPage.steps.advanced,
      title: lang.addLayoutsPage.title,
      component: (
        <FormContainer>
          <AdvancedSettings
            control={control}
            watch={watch}
            setValue={setValue}
            selectedChannelInfo={selectedChannelInfo}
            isOutdoor={isOutdoor}
            codPosition={projectConfig?.codPosition}
          />
        </FormContainer>
      ),
      footer: (
        <StepFooter
          loading={loading}
          handleBack={handleBack}
          handleNext={handleNextAdvanced}
        />
      ),
    },
  ];

  return (
    <Wrapper>
      <GenericStepper steps={steps} activeStep={activeStep} />
    </Wrapper>
  );
};
